import keys from "lodash/keys";
import isString from "lodash/isString";
import isArray from "lodash/isArray";
import isPlainObject from "lodash/isPlainObject";
import queryString from "query-string";
import decodeUriComponent from "decode-uri-component";

/* standardizeQueryParamsObject
--===================================================-- */
// This ensures that search params are in an Array
export function standardizeQueryParamsObject(queryObject) {
  const queryKeys = keys(queryObject);
  let filterObject = queryObject;
  queryKeys.forEach((filterKey) => {
    if (isString(filterObject[filterKey])) {
      let value = filterObject[filterKey];
      filterObject[filterKey] = [value];
    }
  });
  return filterObject;
}

/* sanitizeTrackingParams
--===================================================-- */
const TRACKING_VALUE_MAX_LENGTH = 255;
const ATTRIBUTION_IDENTIFIER_MAX_LENGTH = 1024;
const UTM_DATA_MAX_KEYS = 10;

function sanitizeTrackingValue(value, maxLength = TRACKING_VALUE_MAX_LENGTH) {
  const firstValue = isArray(value) ? value[0] : value; // repeated param: keep first occurrence
  if (!isString(firstValue)) return firstValue;
  const truncatedValue = firstValue.slice(0, maxLength);
  const lastCodeUnit = truncatedValue.charCodeAt(truncatedValue.length - 1);
  return lastCodeUnit >= 0xd800 && lastCodeUnit <= 0xdbff
    ? truncatedValue.slice(0, -1)
    : truncatedValue;
}

function getCookieEntries() {
  const cookieEntries = [];
  document.cookie.split("; ").forEach((cookieEntry) => {
    const separatorIndex = cookieEntry.indexOf("=");
    if (separatorIndex === -1) return;
    const cookieName = cookieEntry.substring(0, separatorIndex);
    const cookieValue = cookieEntry.substring(separatorIndex + 1); // everything after the FIRST "="
    if (cookieValue.length === 0) return; // cookie present with empty value = absent
    cookieEntries.push({ name: cookieName, value: cookieValue });
  });
  return cookieEntries;
}

function getCookieValue(cookieEntries, cookieName) {
  const matchingCookieEntry = cookieEntries.find((cookieEntry) => cookieEntry.name === cookieName);
  return matchingCookieEntry ? matchingCookieEntry.value : undefined;
}

export function sanitizeTrackingParams(search) {
  const parsedParams = queryString.parse(search);

  const keysInOccurrenceOrder = [];
  queryString
    .extract(search)
    .split("&")
    .forEach((param) => {
      if (param.length === 0) return;
      const rawKey = param.replace(/\+/g, " ").split("=")[0];
      const decodedKey = decodeUriComponent(rawKey); // same decoder queryString.parse uses for keys
      if (keysInOccurrenceOrder.indexOf(decodedKey) === -1) {
        keysInOccurrenceOrder.push(decodedKey);
      }
    });

  const trackingParams = {};
  if (parsedParams.utm_source !== undefined) {
    trackingParams.utmSource = sanitizeTrackingValue(parsedParams.utm_source);
  }
  if (parsedParams.utm_campaign !== undefined) {
    trackingParams.utmCampaign = sanitizeTrackingValue(parsedParams.utm_campaign);
  }
  if (parsedParams.internal_ref !== undefined) {
    trackingParams.internalRef = sanitizeTrackingValue(parsedParams.internal_ref);
  }

  const utmDataKeys = keysInOccurrenceOrder
    .filter(
      (key) =>
        key.indexOf("utm_") === 0 &&
        key !== "utm_source" &&
        key !== "utm_campaign" &&
        parsedParams[key] !== undefined,
    )
    .slice(0, UTM_DATA_MAX_KEYS); // first 10 by occurrence order in the raw string

  if (utmDataKeys.length > 0) {
    const utmData = {};
    utmDataKeys.forEach((key) => {
      utmData[key] = sanitizeTrackingValue(parsedParams[key]);
    });
    trackingParams.utmData = utmData;
  }

  return trackingParams;
}

function attributionValue(value) {
  return sanitizeTrackingValue(value, ATTRIBUTION_IDENTIFIER_MAX_LENGTH);
}

function queryParamValue(search, paramName) {
  return attributionValue(queryString.parse(search)[paramName]);
}

function gaClientIdFromCookie(cookieValue) {
  // Expected format for cookie value:  GA1.1.1234567890.1699999999
  const segments = cookieValue ? cookieValue.split(".") : null;
  return segments && segments.length > 3 ? segments.slice(2).join(".") : cookieValue;
}

function gaSessionIdFromCookies(cookieEntries) {
  // One _ga_<CONTAINER_ID> cookie per GA4 property
  const sessionEntries = cookieEntries.filter((cookieEntry) => cookieEntry.name.indexOf("_ga_") === 0);
  return sessionEntries.length
    ? sessionEntries.map((cookieEntry) => `${cookieEntry.name}=${cookieEntry.value}`).join("; ")
    : null;
}

const POSTHOG_COOKIE_NAME_PATTERN = /^ph_.+_posthog$/;

function posthogDistinctIdFromCookies(cookieEntries) {
  // One ph_<PROJECT_API_KEY>_posthog cookie per PostHog project, the value is JSON
  const posthogEntry = cookieEntries.find((cookieEntry) =>
    POSTHOG_COOKIE_NAME_PATTERN.test(cookieEntry.name),
  );
  if (!posthogEntry) return;

  try {
    const distinctId = JSON.parse(decodeURIComponent(posthogEntry.value)).distinct_id;
    if (typeof distinctId !== "string" || distinctId.length === 0) return;

    return distinctId;
  } catch (e) {
    return;
  }
}

// Read fresh at call time, posthog-js writes the cookie a moment after first page load
export function posthogDistinctIdFromCookie() {
  return attributionValue(posthogDistinctIdFromCookies(getCookieEntries()));
}

function googleClickIdFromCookie(cookieValue) {
  // Expected format for cookie value:  GCL.1719852261.actualGoogleClickIdHere
  // Since the beginning can be edited in GTM, get last element of array
  const parsedArray = cookieValue ? cookieValue.split(".") : null;
  return parsedArray && parsedArray.length > 1 ? parsedArray[parsedArray.length - 1] : null;
}

/* adPlatformIdentifiers
--===================================================-- */
export function adPlatformIdentifiers(search) {
  return { ...adPlatformQueryParams(search), ...adPlatformCookies() };
}

/* adPlatformQueryParams
--===================================================-- */
export function adPlatformQueryParams(search) {
  return {
    googleClickId: queryParamValue(search, "gclid"),
    fbclid: queryParamValue(search, "fbclid"),
    liFatId: queryParamValue(search, "li_fat_id"),
    adrollClickId: queryParamValue(search, "adct"),
  };
}

/* adPlatformCookies
--===================================================-- */
export function adPlatformCookies() {
  const cookieEntries = getCookieEntries();

  return {
    gaClientId: attributionValue(gaClientIdFromCookie(getCookieValue(cookieEntries, "_ga"))),
    gaSessionId: attributionValue(gaSessionIdFromCookies(cookieEntries)),
    googleFirstPartyCookie: attributionValue(
      googleClickIdFromCookie(getCookieValue(cookieEntries, "_gcl_aw")),
    ),
    fbp: attributionValue(getCookieValue(cookieEntries, "_fbp")),
    fbc: attributionValue(getCookieValue(cookieEntries, "_fbc")),
    linkedinFirstPartyCookie: attributionValue(getCookieValue(cookieEntries, "li_fat_id")),
    adrollFirstPartyCookie: attributionValue(getCookieValue(cookieEntries, "__adroll_fpc")),
    posthogDistinctId: attributionValue(posthogDistinctIdFromCookies(cookieEntries)),
  };
}

export const trimFilename = (filename) => {
  let cutStart, cutEnd;

  if (filename.length > 20) {
    cutStart = 10;
    cutEnd = filename.length - 7;
    return filename.substr(0, cutStart) + "..." + filename.substr(cutEnd + 1);
  } else {
    return filename;
  }
};

export const clearUrlParams = (history) => {
  history.push({ search: "" });
};

/* URLS
--===================================================-- */
export const addHttpsPrefix = (url) => {
  if (url == undefined) {
    return null;
  }

  // return url.replace(/(^\w+:|^)\/\//, "");
  return url.test(/(^\w+:|^)\/\//, "");
};

export const removeHttpPrefix = (url) => {
  if (url == undefined) {
    return null;
  }

  return url.replace(/(^\w+:|^)\/\//, ""); // removes http:// or https://
};

export const removeKnownUrlPrefix = (url) => {
  if (url == undefined) {
    return null;
  }

  return removeHttpPrefix(url) // removes http:// or https://
    .replace(/(^www\.)/, "") // removes www
    .replace(/(^linkedin\.com\/in\/)/, "") // removes linkedin.com
    .replace(/(^x\.com\/)/, "") // removes x.com
    .replace(/(^twitter\.com\/)/, "") // removes twitter.com (legacy)
    .replace(/(^github\.com\/)/, "") // removes github.com
    .replace(/(^dribbble\.com\/)/, ""); // removes dribbble.com
  // .replace(/(\/$)/, ""); // removes trailing slashes
};

/* Internal links to Polymer marketing properties
--===================================================-- */
export const INTERNAL_UTM_PARAMS = "?utm_source=polymer_app&utm_medium=internal";

/* Remove from Array
--===================================================-- */
export const removeFromArray = (array, fn) => {
  const index = array.findIndex(fn);

  return removeFromArrayAtIndex(array, index);
};

export const removeFromArrayAtIndex = (array, index) => {
  return [...array.slice(0, index), ...array.slice(index + 1, array.length)];
};

export const insertIntoArrayAtIndex = (array, item, index) => {
  console.log("%c[utils] insertIntoArrayAtIndex", "color: #1976D2", { array, item, index });
  return [...array.slice(0, index), item, ...array.slice(index, array.length)];
};

export const moveFromIndexToIndex = (array, fromIndex, toIndex) => {
  const item = array[fromIndex];
  let newArray = removeFromArrayAtIndex(array, fromIndex);
  return insertIntoArrayAtIndex(newArray, item, toIndex);
};

/* Convert Array to Select Options
--===================================================-- */
export const convertArrayToSelectOptions = (options) => {
  return options.map((option, index) => {
    return { value: option, label: option };
  });
};

/* Convert Filter Options to Select Options
--===================================================-- */
// GET /jobs/:job_id/filters serves every option as { value, label } with the label
// built on the backend. The form controls take string option values, because the
// DOM only ever hands a string back, so a boolean value is offered as "true"/"false".
export const convertFilterOptionsToSelectOptions = (options) => {
  return options.map((option) => {
    return { value: String(option.value), label: option.label };
  });
};

/* FORMATTING
--===================================================-- */
export const formatPhoneNumber = (str) => {
  //Filter only numbers from the input
  let cleaned = ("" + str).replace(/\D/g, "");

  //Check if the input is of correct
  let match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    //Remove the matched extension code
    // TODO: Change this to format for any country code.
    let intlCode = match[1] ? "+1 " : "";
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
  }

  return str;
};

/* Check Object for Empty, Null, or Undefined Values
--====================================================-- */
export const objectHasEmptyValues = (obj) => {
  return Object.values(obj).some((value) => {
    if (value === null || value === undefined || value === "") {
      return true;
    }
    if (isArray(value) && value.length === 0) {
      return true;
    }
    if (isPlainObject(value) && keys(value).length === 0) {
      return true;
    }

    return false;
  });
};
